import classnames from 'classnames'
import { useCallback } from 'react'

import { FeatureSectionTwo, IconWithTextProps } from '@src/quince-components/src'
import CustomSection from '@src/quince-site-common/src/domains/CustomSection'
import CUSTOM_SECTION_TYPE from '@src/quince-site-common/src/domains/CustomSectionType'
import RichText from '@src/quince-site-common/src/domains/RichText'
import TrackingEvent from '@src/quince-site-common/src/helpers/tracking-event/tracking-event'
import { computeScrollPercentage } from '@src/quince-site-common/src/hooks/useScrollEventTracker'
import { useTrackIsElementVisible } from '@src/quince-site-common/src/hooks/useTrackIsElementVisible'
import BundlePageSectionProps from '@src/quince-site-pdp/src/domains/BundlePageSectionProps'
import ProductPageSectionProps from '@src/quince-site-pdp/src/domains/ProductPageSectionProps'

import { isFS2SectionDataValid } from '../../../helper/product.helper'

import { useIsFeatureSectionTwoHidden } from './imageWithTextProductDetailsSection.helpers'
import styles from './imageWithTextProductDetailsSection.module.scss'

const FEATURE_SECTION_2_HEADING = 'Feature Section'

function ImageWithTextProductDetailsSection(props: ProductPageSectionProps | BundlePageSectionProps) {
  const product = 'bundle' in props ? props.bundle : props.product

  const desktopSection = product.desktopSections.find(ds => ds.sectionType === CUSTOM_SECTION_TYPE.FEATURE_SECTION_2)
  const mobileSection = product.mobileSections.find(ds => ds.sectionType === CUSTOM_SECTION_TYPE.FEATURE_SECTION_2)

  const isSectionHiddenDesktop = useIsFeatureSectionTwoHidden(desktopSection)
  const isSectionHiddenMobile = useIsFeatureSectionTwoHidden(mobileSection)

  const isDesktopSectionHidden = desktopSection && isFS2SectionDataValid(desktopSection) && isSectionHiddenDesktop
  const isMobileSectionHidden = mobileSection && isFS2SectionDataValid(mobileSection) && isSectionHiddenMobile

  return (
    <>
      {desktopSection && !isDesktopSectionHidden && (
        <div className={classnames(styles.imageWithTextProductDetailsSection, 'desktopOnly')}>
          <Section sectionDetails={desktopSection} />
        </div>
      )}
      {mobileSection && !isMobileSectionHidden && (
        <div className={classnames(styles.imageWithTextProductDetailsSection, 'mobileOnly')}>
          <Section sectionDetails={mobileSection} />
        </div>
      )}
    </>
  )
}

interface SectionProps {
  sectionDetails: CustomSection
}

const Section = ({ sectionDetails }: SectionProps) => {
  const getIsVisibleTrackParams = useCallback(() => {
    return {
      action: 'feature_section_two_viewed',
      value: true,
      context: {
        contentContainerType: sectionDetails.contentContainerType,
        contentContainerName: sectionDetails.contentContainerName,
        trackingId: sectionDetails.trackingId,
        contentContainerDisplayName: sectionDetails.displayTitle,
        is_in_first_fold: computeScrollPercentage() === 0,
      },
    }
  }, [sectionDetails])

  const shouldTrackVisible = sectionDetails.trackingTypes?.includes(TrackingEvent.TRACKING_BROWSER_EVENT_TYPES.VISIBLE)
  const { ref: trackIsVisibleRef } = useTrackIsElementVisible(shouldTrackVisible ? getIsVisibleTrackParams : () => null)

  const iconsWithTextDesktop: IconWithTextProps[] = (sectionDetails.subSections || []).map(pss => ({
    iconProps: {
      title: pss.images ? pss.images[0]?.title || '' : '',
      url: pss.images ? `https:${pss.images[0]?.url}` : '',
    },
    heading: pss.displayTitle || '',
    description: RichText.makeRichTextProps(pss.richTexts ? pss.richTexts[0] : undefined),
  }))

  return (
    <FeatureSectionTwo
      heading={FEATURE_SECTION_2_HEADING}
      imageProps={{
        alt: sectionDetails?.images?.length ? sectionDetails?.images[0]?.title : '',
        title: sectionDetails?.images?.length ? (sectionDetails?.images[0]?.title ?? '') : '',
        url: sectionDetails?.images?.length ? `https:${sectionDetails?.images[0]?.url}` : '',
      }}
      videoProps={{
        id: sectionDetails?.videos?.length ? `https:${sectionDetails?.videos[0]?.id}` : '',
        url: sectionDetails?.videos?.length ? `https:${sectionDetails?.videos[0]?.url}` : '',
        contentType: sectionDetails?.videos?.length ? `https:${sectionDetails?.videos[0]?.contentType}` : '',
      }}
      contents={iconsWithTextDesktop}
      ref={trackIsVisibleRef}
      layoutType={sectionDetails?.layoutType}
      className={classnames(sectionDetails?.classNames)}
    />
  )
}

export default ImageWithTextProductDetailsSection
